import { type AuthErrorCode, authErrorResponseSchema, type CodewordId } from '@airreading/common/error';
import { AxiosError, isAxiosError } from 'axios';

export type SignInFlow = 'google' | 'school';

export interface AuthFailure {
  cause?: unknown;
  code: AuthErrorCode;
  email?: string | null;
  flow?: SignInFlow;
  identityProviderUserId?: string;
  userFacingMessage?: string;
  userId?: string;
}

export type AuthFailureAction = 'start_google_sign_in' | 'return_to_school_portal' | 'reload_application' | 'none';

export interface AuthFailurePresentation {
  title: string;
  description: string;
  codeword: CodewordId;
  action: AuthFailureAction;
  actionLabel?: string;
}

const firebaseCancellationCodes = new Set([
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
  'auth/redirect-cancelled-by-user',
  'auth/user-cancelled',
]);

export function firebaseErrorCode(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null || !('code' in error)) {
    return undefined;
  }
  return typeof error.code === 'string' ? error.code : undefined;
}

export function isFirebaseCancellation(error: unknown): boolean {
  const code = firebaseErrorCode(error);
  return code !== undefined && firebaseCancellationCodes.has(code);
}

export function authFailureFromProviderError(flow: SignInFlow, cause: unknown): AuthFailure | undefined {
  if (isFirebaseCancellation(cause)) {
    return undefined;
  }

  return {
    code:
      firebaseErrorCode(cause)?.startsWith('auth/') === true
        ? flow === 'school'
          ? 'auth_school_provider_error'
          : 'auth_google_provider_error'
        : 'auth_unknown_error',
    flow,
    cause,
  };
}

export function authFailureFromAirLoginError(error: unknown, email?: string | null, flow?: SignInFlow): AuthFailure {
  if (isAxiosError(error)) {
    const status = error.response?.status;
    if (!error.response) {
      return { code: 'auth_network_error', email, flow, cause: error };
    }
    // A successful HTTP status can still carry an unusable API response.
    if (
      status !== undefined &&
      (status >= 500 || (status >= 200 && status < 300 && error.code === AxiosError.ERR_BAD_RESPONSE))
    ) {
      return { code: 'auth_air_unavailable', email, flow, cause: error };
    }

    const parsed = authErrorResponseSchema.safeParse(error.response?.data);
    if (parsed.success) {
      return { code: parsed.data.code, userFacingMessage: parsed.data.message, email, flow, cause: error };
    }
  }

  return { code: 'auth_unknown_error', email, flow, cause: error };
}

export function authFailurePresentation(failure: AuthFailure): AuthFailurePresentation {
  switch (failure.code) {
    case 'auth_account_not_found':
      return {
        title: 'Account not found',
        description:
          failure.userFacingMessage ??
          `We could not find an account for ${failure.email ?? 'this email address'}. Please contact Air Reading support for help logging in.`,
        codeword: 'LEMON',
        action: failure.flow === 'school' ? 'return_to_school_portal' : 'start_google_sign_in',
        actionLabel: failure.flow === 'school' ? 'Return to school portal' : 'Try another account',
      };
    case 'auth_session_expired':
      return {
        title: 'Session expired',
        description: failure.userFacingMessage ?? 'Your session has expired. Please sign in again.',
        codeword: 'TREE',
        action: failure.flow === 'school' ? 'return_to_school_portal' : 'start_google_sign_in',
        actionLabel: failure.flow === 'school' ? 'Return to school portal' : 'Continue with Google',
      };
    case 'auth_google_provider_error':
      return {
        title: 'Sign in with Google error',
        description: 'There was an error signing in with Google. Please try again.',
        codeword: 'PANDA',
        action: 'start_google_sign_in',
        actionLabel: 'Try again',
      };
    case 'auth_school_provider_error':
      return {
        title: 'School sign in error',
        description:
          'There was an error signing in through your school. Please return to your school portal and try again.',
        codeword: 'ZEBRA',
        action: 'return_to_school_portal',
        actionLabel: 'Return to school portal',
      };
    case 'auth_unknown_email_not_approved':
      return {
        title: 'Please wait',
        description: failure.userFacingMessage ?? 'Please wait, we are verifying your Air Reading account.',
        codeword: 'CLOCK',
        action: 'none',
      };
    case 'auth_identity_inactive':
      return {
        title: 'Account disabled',
        description:
          failure.userFacingMessage ??
          `The account for ${failure.email ?? 'this email address'} has been disabled. Please contact Air Reading support.`,
        codeword: 'RAINBOW',
        action: 'none',
      };
    case 'auth_network_error':
      return {
        title: 'Connection lost',
        description: 'We could not connect to Air Reading. Check your internet connection and reload to try again.',
        codeword: 'BALLOON',
        action: 'reload_application',
        actionLabel: 'Reload Air Reading',
      };
    case 'auth_air_unavailable':
      return {
        title: 'Air Reading is unavailable',
        description: 'Air Reading encountered a server error. Please wait a moment and reload to try again.',
        codeword: 'PUZZLE',
        action: 'reload_application',
        actionLabel: 'Reload Air Reading',
      };
    case 'auth_csrf_invalid':
      return {
        title: 'Request verification failed',
        description: 'We could not verify your request. Please reload Air Reading to try again.',
        codeword: 'BIKE',
        action: 'reload_application',
        actionLabel: 'Reload Air Reading',
      };
    case 'auth_unknown_error':
      return {
        title: 'Sign in error',
        description: failure.userFacingMessage ?? 'There was an error signing in. Please try again.',
        codeword: 'OCEAN',
        action:
          failure.flow === 'school'
            ? 'return_to_school_portal'
            : failure.flow === 'google'
              ? 'start_google_sign_in'
              : 'reload_application',
        actionLabel:
          failure.flow === 'school'
            ? 'Return to school portal'
            : failure.flow === 'google'
              ? 'Continue with Google'
              : 'Reload Air Reading',
      };
  }
}
